import auth from '@react-native-firebase/auth';
import { UserServiceClient } from 'fitbeat.api-clients';
import {
  INavigateToNextStepOptions,
  SignupFlowType,
  SignupScreen,
  UserProfile,
} from 'fitbeat.gym-core';
import {
  EnabledFeatures, Gym, IEnabledFeatures, IUserExistsCheck,
  RemoteConfigVariables,
} from 'fitbeat.models';
import { Platform } from 'react-native';
import Product from 'react-native-iaphub/src/models/product'; // todo: need to not import from their internal models
import { Action } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { appSignupAgreementSet } from './components/utils';
import { IAppStore } from '../HomeWorkouts';
import { GymResolver } from '../services';
import { analyicsForAction } from '../services/google-analytics/analyicsForAction';
import { InAppPurchaseService } from '../services/iap';
import { initializeTheme } from '../theme';
import { showError } from '../ui/components/Toast';
import { Storage } from '../utils';
import { createUnauthenticatedUserServiceClient } from '../utils/apiClientFactory';
import { FirebaseRemoteConfig } from '../utils/FirebaseRemoteConfig';
import { Logger } from '../utils/Logger';
import { addDeepLinkParams } from '../utils/notifications';
import { clearDeepLinkParams } from '../utils/notifications/actions';
import { retryFunctionWithExponentialBackoff } from '../utils/retry-function';
import * as actionTypes from './actionTypes';
import {
  INavigateToNextStep, InviteAcceptStatus, ISignupNextScreenAction, SignupActionTypes, UserActionTypes,
} from './types';

export const setBasicUserInformation = async (basicUserInfo) => {
  await Storage.set('basicUserInfo', JSON.stringify(basicUserInfo));
  return {
    type: actionTypes.SET_BASIC_USER_INFO,
    payload: {
      basicUserInfo,
    },
  };
};

export const setGuardianInformation = async (guardianInfo) => {
  await Storage.set('guardianInfo', JSON.stringify(guardianInfo));
  return {
    type: actionTypes.SET_GUARDIAN_INFORMATION,
    payload: {
      guardianInfo,
    },
  };
};

export const setSignupError = (signupError) => async (dispatch) => {
  dispatch({
    type: actionTypes.SIGNUP_ERROR,
    payload: {
      signupError,
    },
  });
};

export const clearSignupError = () => async (dispatch) => {
  dispatch({
    type: actionTypes.CLEAR_SIGNUP_ERROR,
  });
};

export const garminConnectAcceptedAuthorizationRequest = (): UserActionTypes => ({
  type: actionTypes.GARMIN_ACCEPTED_AUTHORIZATION_REQUEST,
});

export const garminConnectAcceptedAuthorizationSuccess = (isAuthorized: boolean): UserActionTypes => ({
  type: actionTypes.GARMIN_ACCEPTED_AUTHORIZATION_SUCCESS,
  payload: {
    isAuthorized,
  },
});

export const garminConnectAcceptedAuthorizationFailure = (): UserActionTypes => ({
  type: actionTypes.GARMIN_ACCEPTED_AUTHORIZATION_FAILURE,
});

export const signupFlowScreenRequest = (): SignupActionTypes => ({
  type: actionTypes.SIGNUP_FLOW_SCREEN_REQUEST,
});

export const signupFlowScreenSuccess = (screens: SignupScreen[]): SignupActionTypes => ({
  type: actionTypes.SIGNUP_FLOW_SCREEN_SUCCESS,
  payload: screens,
});

export const signupFlowScreenFailure = (): SignupActionTypes => ({
  type: actionTypes.SIGNUP_FLOW_SCREEN_FAILURE,
});

export const loadUserRequest = (): UserActionTypes => ({
  type: actionTypes.LOAD_USER_REQUEST,
});

export const loadUserSuccess = (enabledFeatures: IEnabledFeatures, userProfile: UserProfile): UserActionTypes => ({
  type: actionTypes.LOAD_USER_SUCCESS,
  payload: {
    enabledFeatures,
    userProfile,
  },
});

export const loadUserFailure = (): UserActionTypes => ({
  type: actionTypes.LOAD_USER_FAILURE,
});

export const setIsInitialDataLoaded = (): UserActionTypes => ({
  type: actionTypes.SET_IS_INITIAL_DATA_LOADED,
});

export const getSignupFlowScreens = (
  userProfileService: UserServiceClient,
  signupFlowType: SignupFlowType,
): ThunkAction<void, IAppStore, unknown, Action<string>> => async (dispatch) => {
  try {
    dispatch(signupFlowScreenRequest());
    const { screens } = await userProfileService.getSignupFlowScreens(signupFlowType);
    dispatch(signupFlowScreenSuccess(screens));
  } catch (error) {
    dispatch(signupFlowScreenFailure());
  }
};

export const nextScreenStep = (
  currentScreen: SignupScreen,
  skipRestOfFlow: boolean,
  userProfile: Partial<UserProfile>,
  gym: Gym | null,
  inviteAcceptStatus?: InviteAcceptStatus,
  options?: INavigateToNextStepOptions,
): ISignupNextScreenAction => ({
  type: actionTypes.NEXT_SCREEN_STEP,
  payload: {
    currentScreen,
    userProfile,
    skipRestOfFlow,
    inviteAcceptStatus,
    gym,
    options,
  },
});

const updateUserProfileSuccess = (): SignupActionTypes => ({
  type: actionTypes.UPDATE_USER_PROFILE_SUCCESS,
});

const updateUserProfileFailure = (error: string): SignupActionTypes => ({
  type: actionTypes.UPDATE_USER_PROFILE_FAILURE,
  payload: error,
});

export const getNextStep = ({
  getMealData,
  currentScreen,
  userProfile,
  userServiceClient,
  skipRestOfFlow = false,
  inviteAcceptStatus,
  options,
}: INavigateToNextStep): ThunkAction<void, IAppStore, unknown, Action<string>> => async (dispatch, getState) => {
  const { auth: { signup: { userProfile: userBeforeUpdate } } } = getState();
  const gym = userBeforeUpdate?.gymName ? GymResolver.getByNameOrNull(userBeforeUpdate.gymName) : null;
  dispatch(nextScreenStep(currentScreen, skipRestOfFlow, userProfile, gym, inviteAcceptStatus, options));

  try {
    const { auth } = getState();
    const {
      signup: {
        userProfile: existingUserProfile, shouldPersistUser, userProfileFieldsToUpdateAfterOnboarding,
      },
    } = auth;
    const profileToUpdate = { ...existingUserProfile, ...userProfileFieldsToUpdateAfterOnboarding };
    if (shouldPersistUser && userServiceClient) {
      await userServiceClient.updateUserProfile(profileToUpdate, /* updatedAtOnboardingComplete */ true);
      dispatch(updateUserProfileSuccess());

      if (getMealData) {
        getMealData();
      }
    }
  } catch (error) {
    dispatch(updateUserProfileFailure(error.message));
  }
};

export const appOnboardingStarted = (userProfile: UserProfile, enabledFeatures: EnabledFeatures) => ({
  type: actionTypes.APP_ONBOARDING_STARTED,
  payload: { userProfile, enabledFeatures },
});

const onScreenComplete = (openInstantWorkout?: boolean): SignupActionTypes => ({
  type: actionTypes.ON_SCREEN_COMPLETE,
  payload: !!openInstantWorkout,
});

export const navigateToNextStep = (
  navigation: any,
  options?: INavigateToNextStepOptions,
): ThunkAction<void, IAppStore, unknown, Action<string>> => async (dispatch, getState) => {
  const { auth: { signup } } = getState();
  if (options?.isFirstScreenOnStack) {
    navigation.replace(signup.signupScreenWithIndex.screen);
  } else {
    navigation.navigate(signup.signupScreenWithIndex.screen);
  }
  dispatch(onScreenComplete(options?.openInstantWorkout));
};

export const getHomeProduct = (
  userId: string,
  inAppPurchaseService: typeof InAppPurchaseService,
  userServiceClient: UserServiceClient,
): ThunkAction<void, IAppStore, unknown, Action<string>> => async (dispatch, getState: () => IAppStore) => {
  if (Platform.OS === 'web') {
    const { auth: { signup } } = getState();
    getStripeHomePlan(dispatch, userId, signup.defaultHomePlanId, userServiceClient)
      .catch((e) => Logger.notify(e));
  } else {
    getIapProducts(dispatch, userId, inAppPurchaseService)
      .catch((e) => Logger.notify(e));
  }
};

export const setInitialDataLoaded = (): ThunkAction<void, IAppStore, unknown, Action<string>> => (dispatch) => {
  dispatch(setIsInitialDataLoaded());
};

export const loadUser = (userServiceClient: UserServiceClient, userProfile: UserProfile) => async (dispatch) => {
  try {
    dispatch(loadUserRequest());
    const enabledFeatures = await userServiceClient.getUserEnabledFeatures();
    const allGyms = await userServiceClient.getAllGyms();
    GymResolver.prefill(allGyms);
    initializeTheme(enabledFeatures.organisationConfig?.theme);
    dispatch(loadUserSuccess(enabledFeatures, userProfile));
  } catch (error) {
    dispatch(loadUserFailure());
  }
};

const getStripeHomePlan = async (dispatch, userId: string, planId: string, userServiceClient: UserServiceClient) => {
  try {
    dispatch(stripeHomePlanSummaryRequest());
    const coupon = undefined;
    const onceOffPriceIds = undefined;
    const paymentSubscriptionSummary = await userServiceClient.getSubscriptionPaymentSummary(
      userId,
      planId,
      coupon,
      onceOffPriceIds,
    );
    dispatch(stripeHomePlanSummarySuccess(paymentSubscriptionSummary));
  } catch (error) {
    dispatch(stripeHomePlanSummaryFailure(error.message));
  }
};

export const stripeHomePlanSummaryRequest = (): SignupActionTypes => ({
  type: actionTypes.STRIPE_HOME_PLAN_SUMMARY_REQUEST,
});

export const stripeHomePlanSummarySuccess = (
  paymentSubscriptionSummary: any,
): SignupActionTypes => ({
  type: actionTypes.STRIPE_HOME_PLAN_SUMMARY_SUCCESS,
  payload: paymentSubscriptionSummary,
});

export const stripeHomePlanSummaryFailure = (error: string): SignupActionTypes => ({
  type: actionTypes.STRIPE_HOME_PLAN_SUMMARY_FAILURE,
  payload: error,
});

const initIAPAndFetchProducts = async (
  userId: string,
  inAppPurchaseService: typeof InAppPurchaseService,
) => {
  await inAppPurchaseService.init(userId);
  const productsForSale = await inAppPurchaseService.getProductsForSale();
  const activeProducts = await inAppPurchaseService.getActiveProducts();
  return { productsForSale, activeProducts };
};

const getIapProducts = async (
  dispatch,
  userId: string,
  inAppPurchaseService: typeof InAppPurchaseService,
) => {
  try {
    Logger.debug('Auth/Actions: getIapProducts', { userId });
    dispatch(iapProductsRequest());
    const { productsForSale, activeProducts } = await retryFunctionWithExponentialBackoff(
      () => initIAPAndFetchProducts(userId, inAppPurchaseService),
    );
    const homeInAppSubscriptionPriceId = await FirebaseRemoteConfig
      .getRemoteConfigValue(RemoteConfigVariables.iap_hub_product_id_fitillion);
    const productSku = homeInAppSubscriptionPriceId.toLowerCase();
    dispatch(iapProductsSuccess(productSku, productsForSale, activeProducts));
  } catch (error) {
    Logger.notify(error, (report) => {
      report.errorClass = 'getIapProducts';
      report.errorMessage = JSON.stringify(error);
    });
    dispatch(iapProductsFailure(error.message));
  }
};

export const iapProductsRequest = (): SignupActionTypes => ({
  type: actionTypes.IAP_PRODUCTS_REQUEST,
});

export const iapProductsSuccess = (
  productSku: string,
  productsForSale: Product[],
  activeProducts: Product[],
): SignupActionTypes => ({
  type: actionTypes.IAP_PRODUCTS_SUCCESS,
  payload: {
    productSku,
    productsForSale,
    activeProducts,
  },
});

export const iapProductsFailure = (error: string): SignupActionTypes => ({
  type: actionTypes.IAP_PRODUCTS_FAILURE,
  payload: error,
});

export const iapBuyRequest = (): SignupActionTypes => ({
  type: actionTypes.IAP_BUY_REQUEST,
});

export const iapBuySuccess = (): SignupActionTypes => ({
  type: actionTypes.IAP_BUY_SUCCESS,
});

export const iapBuyFailure = (error: string): SignupActionTypes => ({
  type: actionTypes.IAP_BUY_FAILURE,
  payload: error,
});

export const buyIapProduct = (
  inAppPurchaseService: typeof InAppPurchaseService,
  onSuccess: () => void,
): ThunkAction<void, IAppStore, unknown, Action<string>> => async (dispatch, getState) => {
  try {
    const { auth: { signup } } = getState();
    const { product } = signup.iap;
    if (product) {
      dispatch(iapBuyRequest());
      await inAppPurchaseService.buy(product.sku);
      await onSuccess();
      dispatch(iapBuySuccess());
    } else {
      throw new Error('Trying to join unknown product');
    }
  } catch (error) {
    Logger.notify(error);
    dispatch(iapBuyFailure(error.message));
  }
};

// Todo: extract create user logic from CreatePreAccount component to this action
export const preAccountCreateSubmit = () => ({
  type: actionTypes.PRE_ACCOUNT_CREATE_SUBMIT,
  ...analyicsForAction(actionTypes.PRE_ACCOUNT_CREATE_SUBMIT),
});

export const captureEmailFormSubmitted = (
  email: string,
  unAuthenticatedUserServiceClient: UserServiceClient,
  navigate: (route: string) => void,
) => async (dispatch) => {
  try {
    dispatch({ type: actionTypes.CAPTURE_EMAIL_SUBMIT_REQUEST });
    const userExistsCheck: IUserExistsCheck = await unAuthenticatedUserServiceClient.getUserForPreAccountByEmail(email);
    if (!userExistsCheck) {
      dispatch({ type: actionTypes.CAPTURE_EMAIL_SUBMIT_FAILURE, payload: { error: 'registerBeforeContinuing' } });
    } else if (userExistsCheck?.continueSignupToken) {
      dispatch({ type: actionTypes.CAPTURE_EMAIL_SUBMIT_SUCCESS, payload: { userExistsCheck } });
      navigate('PreAccountSetPassword');
    } else if (userExistsCheck?.userId) {
      dispatch({ type: actionTypes.CAPTURE_EMAIL_SUBMIT_SUCCESS, payload: { userExistsCheck } });
      dispatch(addDeepLinkParams({ email }));
      navigate('Login');
    } else {
      dispatch({ type: actionTypes.CAPTURE_EMAIL_SUBMIT_SUCCESS, payload: { userExistsCheck } });
    }
  } catch (e) {
    dispatch({ type: actionTypes.CAPTURE_EMAIL_SUBMIT_FAILURE });
  }
};

const createFirebaseUserIfNotExistSignInAndUpdatePassword = async (
  continueSignupToken: string,
  newPassword: string,
) => {
  const unAuthenticatedUserServiceClient = createUnauthenticatedUserServiceClient();
  const tempCredentials = await unAuthenticatedUserServiceClient.continueSignup(
    continueSignupToken,
    appSignupAgreementSet,
  );
  await auth().signInWithEmailAndPassword(tempCredentials.email, tempCredentials.password);
  await auth().currentUser.updatePassword(newPassword);
};

export const preAccountSetPasswordFormSubmitted = (
  password: string,
  navigate: (route: string) => void,
) => async (dispatch, getState: () => IAppStore) => {
  try {
    dispatch({ type: actionTypes.PRE_ACCOUNT_SET_PASSWORD_REQUEST });
    const state = getState();
    const continueSignupToken = state.auth.signup.userExistsCheckData?.continueSignupToken;
    if (!continueSignupToken) {
      throw new Error('continue signup token required for pre account set password');
    }
    await createFirebaseUserIfNotExistSignInAndUpdatePassword(continueSignupToken, password);
    navigate('HomeNavigator');
    dispatch({ type: actionTypes.PRE_ACCOUNT_SET_PASSWORD_SUCCESS });
  } catch (e) {
    Logger.notify(e);
    dispatch({ type: actionTypes.PRE_ACCOUNT_SET_PASSWORD_FAILURE });
  }
};

const getUserByContinueSignupToken = (continueSignupToken): Promise<UserProfile> => {
  const unAuthenticatedUserServiceClient = createUnauthenticatedUserServiceClient();
  return unAuthenticatedUserServiceClient.getUserBySignupToken(continueSignupToken);
};

export const preAccountSetPasswordScreenMounted = (
  replaceRoute: (route: string) => void,
) => async (dispatch, getState: () => IAppStore) => {
  const { continueSignupToken } = getState().deepLinkParamaters;
  if (!continueSignupToken) {
    return;
  }
  try {
    dispatch({ type: actionTypes.SET_PASSWORD_SCREEN_MOUNTED_REQUEST });
    const user = await getUserByContinueSignupToken(continueSignupToken);
    const userExistsCheck: IUserExistsCheck = {
      continueSignupToken: user.continueSignupToken,
      userId: user.uid,
      firstname: user.firstname,
    };
    dispatch({ type: actionTypes.SET_PASSWORD_SCREEN_MOUNTED_SUCCESS, payload: { userExistsCheck } });
  } catch (e) {
    showError((e as Error).message);
    dispatch(clearDeepLinkParams(['continueSignupToken']));
    replaceRoute('CaptureEmail');
    dispatch({ type: actionTypes.SET_PASSWORD_SCREEN_MOUNTED_FAILURE });
  }
};

export const installAppScreenMounted = () => async (dispatch, getState: () => IAppStore) => {
  const { continueSignupToken } = getState().deepLinkParamaters;
  if (!continueSignupToken) {
    return;
  }
  try {
    dispatch({ type: actionTypes.INSTALL_APP_SCREEN_MOUNTED_REQUEST });
    const userProfile = await getUserByContinueSignupToken(continueSignupToken);
    dispatch({
      type: actionTypes.INSTALL_APP_SCREEN_MOUNTED_SUCCESS,
      payload: {
        userProfile,
      },
    });
  } catch (e) {
    showError((e as Error).message);
    dispatch({ type: actionTypes.INSTALL_APP_SCREEN_MOUNTED_FAILURE });
  }
};
